import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { Division, Service } from '@/services/api/services-api';
import { useState } from 'react';
import { isSameDay } from 'date-fns';
import { getFormatDate, getFormatTime, getTimeZone } from '@/utils/date';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Extended type for range divisions that includes additional properties
type RangeDivision = Division & {
  endTime?: string;
  startHour?: string;
};

type Props = {
  form: UseFormReturn<any>;
  organization: TOrganizationFAQ;
  service: Service;
  isLanding: boolean;
  isMultiple?: boolean;
  tz: string;
};

export const BookingByGroups = ({
  form,
  service,
  isMultiple = true,
  tz
}: Props) => {
  const { t } = useTranslation(['newBooking', 'common']);
  const rangeDivisions = [...(service.rangeDivisions ?? [])].sort((a, b) => a.order - b.order);
  const [selectedRanges, setSelectedRanges] = useState<string[]>([]);
  const handleRangeSelected = (rangeId: string) => {
    // Toggle selection: add if not selected, remove if already selected
    if (isMultiple) {
      // Calculate the new state synchronously
      const isAlreadySelected = selectedRanges.includes(rangeId);
      const updatedSelection = isAlreadySelected
        ? selectedRanges.filter((id) => id !== rangeId)
        : [...selectedRanges, rangeId];
      // Update both local state and form values with the same data
      setSelectedRanges(updatedSelection);
      form.setValue('serviceDivisionGroups', updatedSelection);

      // Trigger form validation
      form.trigger('serviceDivisionGroups');
    } else {
      setSelectedRanges([rangeId]);
      form.setValue('serviceDivisionGroups', [rangeId]);
      form.trigger('serviceDivisionGroups');
    }
  };

  return (
    <FormField
      control={form.control}
      name="serviceDivisionGroups"
      render={({
        field
      }: {
        field: ControllerRenderProps<any, 'serviceDivisionGroups'>;
      }) => (
        <FormItem>
          <FormControl>
            <div className="flex flex-col gap-4 mb-6" ref={field.ref}>
              <h3 className="text-lg font-bold">
                {`${
                  isMultiple
                    ? t('divisionGroups.title')
                    : t('divisionGroups.titleSingle')
                }`}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(rangeDivisions as RangeDivision[])?.map((range) => {
                  // Obtener timestamps como números
                  const startTime = Number((range as any).startTime);
                  const endTime = Number((range as any).endTime);

                  // Formatear fechas con timezone si está disponible
                  let startDateStr = '';
                  let endDateStr = '';
                  let sameDay = false;

                  if (tz && startTime) {
                    startDateStr = getFormatDate(startTime, tz);
                    if (endTime) {
                      endDateStr = getFormatDate(endTime, tz);
                      // Comparar solo las fechas (sin hora)
                      const startZoned = getTimeZone(startTime, tz);
                      const endZoned = getTimeZone(endTime, tz);
                      sameDay = isSameDay(startZoned, endZoned);
                    }
                  } else {
                    // Fallback sin timezone
                    startDateStr = new Date(startTime).toLocaleDateString();
                    if (endTime) {
                      endDateStr = new Date(endTime).toLocaleDateString();
                      sameDay = isSameDay(
                        new Date(startTime),
                        new Date(endTime)
                      );
                    }
                  }

                  return (
                    <div
                      key={range.id}
                      className={cn(
                        'p-4 border rounded-md cursor-pointer transition-colors',
                        !range.active
                          ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                          : selectedRanges.includes(range.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'hover:border-blue-200 hover:bg-blue-50'
                      )}
                      onClick={() =>
                        range.active && handleRangeSelected(range.id)
                      }
                    >
                      <h4 className="font-medium text-lg">{range.name}</h4>
                      <p className="text-gray-600">{range.description}</p>
                      <p className="text-gray-600 text-sm">
                        {startDateStr}
                        {endTime && !sameDay && (
                          <>
                            &nbsp;{t('divisionGroups.to2')} {endDateStr}
                          </>
                        )}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {t('divisionGroups.from')}{' '}
                        {(() => {
                          // Si tenemos startHour explícito, usarlo
                          if (range.startHour) {
                            return range.startHour;
                          }
                          // Si tenemos timezone y timestamp, formatear con timezone
                          if (tz && startTime) {
                            return getFormatTime(startTime, tz);
                          }
                          // Fallback
                          return '08:00';
                        })()}{' '}
                        {t('divisionGroups.to')}{' '}
                        {(() => {
                          // Si tenemos startHour y duration, calcular hora fin
                          if (range.startHour && range.duration) {
                            const [hours, minutes] = range.startHour
                              .split(':')
                              .map(Number);
                            const totalMinutes =
                              hours * 60 + minutes + range.duration;
                            const endHour = Math.floor(totalMinutes / 60) % 24;
                            const endMin = totalMinutes % 60;
                            return `${endHour
                              .toString()
                              .padStart(2, '0')}:${endMin
                              .toString()
                              .padStart(2, '0')}`;
                          }
                          // Si tenemos timezone, timestamp y duration
                          if (tz && startTime && range.duration) {
                            const endTime =
                              startTime + range.duration * 60 * 1000;
                            return getFormatTime(endTime, tz);
                          }
                          // Fallback
                          return '12:00';
                        })()}{' '}
                        hs
                      </p>
                      <span
                        className={cn(
                          'inline-block mt-2 px-2 py-1 text-xs rounded-full',
                          range.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        )}
                      >
                        {range.active
                          ? t('divisionGroups.available')
                          : t('divisionGroups.unavailable')}
                      </span>
                    </div>
                  );
                })}
              </div>

              {form.formState.errors.serviceDivisionGroups && (
                <p className="text-red-500 text-sm mt-1">
                  {isMultiple
                    ? t('divisionGroups.error')
                    : t('divisionGroups.errorSingle')}
                </p>
              )}
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
};
